import {zodResolver} from "@hookform/resolvers/zod";
import {
    Button,
    Card,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import {RhfCheckbox, RhfTextField} from "mui-rhf-integration";
import {type Dispatch, type SetStateAction, useContext, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {z} from "zod";
import FeeQuestionsForm from "@/components/Forms/FeeQuestionsForm.tsx";
import RhfNumericTextField from "@/components/Forms/Fields/RhfNumericTextField.tsx";
import {useFeeProposalsProvider} from "@/components/Providers/InstructorFeeProposalsProvider.tsx";
import {jwtInstructorContext} from "@/components/Providers/JWTProvider.tsx";
import useSaveFeeProposal, {type SaveFeeResult} from "@/hooks/useSaveFeeProposal.tsx";
import type {Employee} from "@/types/Employee.ts";
import "./FeeQuestionsForm.css";
import ReactMarkdown from "react-markdown";
import {WAIVER} from "@/components/Fees/WaiverQuestion.tsx";
import MuiDatePicker from "@/components/Forms/Fields/MuiDatePicker.tsx";
import SponsorList from "@/components/Forms/SponsorList.tsx";
import type {BaseAlert} from "@/components/GlenSnackBar.tsx";
import {useNotice} from "@/components/Providers/NoticeProvider.tsx";
import type {FeeQuestion} from "@/types/FeeQuestion.ts";
import type {FeeRequest, FeeRequestDefaults} from "@/types/FeeRequest.ts";
import type {School} from "@/types/School.ts";
import type {SchoolYear} from "@/types/SchoolYear.ts";

export interface FeeProposalFormValues {
    feeRequestId?: string;
    name: string;
    school: string;
    year: string;
    category?: string;
    approvalRoute: string;
    type: string;
    cost: number;
    costVariable: boolean;
    accountNumber?: string;
    accountAssignedByBusinessOffice?: boolean;
    allowStudent: boolean;
    allowEmployee: boolean;
    allowCommunityMember: boolean;
    allowGuest: boolean;
    allowMultiplePurchases: boolean;
    collectionStart: Date;
    collectionEnd: Date;
    description: string;
    otherInformation?: string;
    sponsors: Employee[];
    requiresAdditionalInformation: string;
    questions: FormQuestion[];
}

export type FormQuestion = Omit<FeeQuestion, "sortOrder"> & {
    sortOrder: string;
};

const feeProposalRequestSchema = z
    .object({
        feeRequestId: z.string().uuid().optional(),
        name: z.string().min(1),
        school: z.string(),
        year: z.string(),
        category: z.string().optional(),
        approvalRoute: z.string(),
        type: z.string(),
        cost: z.coerce
            .number({required_error: "Required", invalid_type_error: "Required"})
            .min(1, "Cost must be at least $1.00"),
        costVariable: z.boolean(),
        accountNumber: z.string().optional(),
        allowStudent: z.boolean(),
        allowEmployee: z.boolean(),
        allowCommunityMember: z.boolean(),
        allowGuest: z.boolean(),
        allowMultiplePurchases: z.boolean(),
        accountAssignedByBusinessOffice: z.boolean().optional(),
        collectionStart: z
            .date()
            .transform(d => {
                d.setHours(0, 0, 0, 0);
                return d;
            })
            .refine(date => date && !(date < new Date()), {
                message: "Date must be in the future.",
            }),
        collectionEnd: z
            .date()
            .transform(d => {
                d.setHours(23, 59, 59, 0);
                return d;
            })
            .refine(date => date && !(date < new Date()), {
                message: "Date must be in the future.",
            }),
        description: z.string(),
        otherInformation: z.string().optional(),
        sponsors: z
            .array(
                z.object({
                    id: z.string(),
                    name: z.string(),
                }),
            )
            .min(1, "You must have at least one sponsor."),
        requiresAdditionalInformation: z.string(),
        questions: z
            .array(
                z
                    .object({
                        question: z.string().min(1, "This field is required."),
                        responseType: z.string().min(1, "Response Type is required."),
                        answerList: z.string(),
                        sortOrder: z.string(),
                    })
                    .refine(data => data.responseType !== "Value List" || data.answerList !== "", {
                        message:
                            "'Value List Options' is required when 'Question Type' is 'Value List'",
                        path: ["answerList"],
                    }),
            )
            .superRefine((questions, ctx) => {
                questions.forEach((item, idx) => {
                    const firstIdx = questions.findIndex(q => q.sortOrder === item.sortOrder);
                    if (firstIdx !== idx) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Duplicate sortOrder: ${item.sortOrder}`,
                            path: [idx, "sortOrder"],
                        });
                    }
                });
            })
            .superRefine((questions, ctx) => {
                const waiverQuestions = questions.filter(q => q.responseType === WAIVER);
                if (waiverQuestions.length > 1) {
                    let foundFirstWaiver = false;
                    for (const index in questions) {
                        const waiver = questions[index];
                        if (waiver.responseType === WAIVER) {
                            if (foundFirstWaiver) {
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    message: "You can only add one Waiver.",
                                    path: [`[${index}].responseType`],
                                });
                            } else {
                                foundFirstWaiver = true;
                            }
                        }
                    }
                }
            }),
    })
    .superRefine((data, ctx) => {
        console.log("data.requiresAdditionalInformation", data.requiresAdditionalInformation);
        if (
            data.requiresAdditionalInformation &&
            data.requiresAdditionalInformation === "yes" &&
            data.questions.length === 0
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    "There must be at least one question when Requires Additional Information is Yes.",
                path: ["requiresAdditionalInformation"],
            });
        }

        if (data.type === "Optional" && !data.category) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Category is required when Type is set Optional",
                path: ["category"],
            });
        }

        if (
            data.collectionStart &&
            data.collectionEnd &&
            (data.collectionStart > data.collectionEnd ||
                data.collectionEnd < data.collectionStart ||
                data.collectionStart === data.collectionEnd)
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Collection start date must be before collection end date",
                path: ["collectionEnd"],
            });
        }

        if (!(data.accountAssignedByBusinessOffice || data.accountNumber)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Account Number required if not assigned by BO",
                path: ["accountNumber"],
            });
        }
    });

export type FeeProposalRequestSchemaType = z.infer<typeof feeProposalRequestSchema>;

type Props = {
    show: boolean;
    setShow: (show: boolean) => void;
    feeRequest?: FeeRequest | null;
    feeRequestDefaults: FeeRequestDefaults | undefined;
    setFeeRequest: Dispatch<SetStateAction<FeeRequest | null>>;
    setFeeNotification: (alert: BaseAlert | null) => void;
};

const toIdString = (related: {id: string | number} | null | undefined): string | undefined => {
    return related ? related.id.toString() : undefined;
};

const isSelectableSchool = (school: School): boolean => {
    return !school.excludeFromFeeRequest;
};

const isSelectableSchoolYear = (schoolYear: SchoolYear): boolean => {
    return !schoolYear.excludeFromFeeRequest;
};

const withSavedOption = <T extends {id: string}>(
    options: T[],
    all: T[],
    savedId: string | undefined,
): T[] => {
    // School.id is typed string but the column is an int, so compare as strings
    if (!savedId || options.some(option => `${option.id}` === savedId)) {
        return options;
    }

    const saved = all.find(option => `${option.id}` === savedId);

    return saved ? [...options, saved] : options;
};

const resolveDefaultSchoolId = (
    feeRequest: FeeRequest | null | undefined,
    feeRequestDefaults: FeeRequestDefaults | undefined,
    schools: School[] | null | undefined,
): string | undefined => {
    if (feeRequest) {
        return toIdString(feeRequest.school);
    }
    const schoolId = toIdString(feeRequestDefaults?.school);
    if (
        schoolId &&
        schools?.some(school => `${school.id}` === schoolId && isSelectableSchool(school))
    ) {
        return schoolId;
    }
    return undefined;
};

const resolveDefaultYearId = (
    feeRequest: FeeRequest | null | undefined,
    feeRequestDefaults: FeeRequestDefaults | undefined,
    schoolYears: SchoolYear[] | null | undefined,
): string | undefined => {
    if (feeRequest) {
        return feeRequest.schoolYear.id;
    }
    const yearId = feeRequestDefaults?.schoolYear?.id;
    if (
        yearId &&
        schoolYears?.some(
            schoolYear => schoolYear.id === yearId && isSelectableSchoolYear(schoolYear),
        )
    ) {
        return yearId;
    }
    return undefined;
};

const feeTypeLabel = (feeType: string | null | undefined): string => {
    const value = feeType ?? "optional";
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const resolveDefaultType = (
    feeRequest: FeeRequest | null | undefined,
    feeType: string | null | undefined,
): string | undefined => {
    const type = feeTypeLabel(feeType);
    return !feeRequest && type === "Course" ? undefined : type;
};
const FeeProposalRequestForm = ({
    show,
    setShow,
    feeRequest = null,
    setFeeRequest,
    feeRequestDefaults,
    setFeeNotification,
}: Props) => {
    const [notice] = useNotice();
    const {user} = useContext(jwtInstructorContext);
    const {loading, schools, schoolYears, approvalRoutes, employees, feeCategories, refresh} =
        useFeeProposalsProvider();
    const saveFeeProposal = useSaveFeeProposal();
    const [saving, setSaving] = useState(false);

    const currentEmployee = useMemo(() => {
        if (!employees) {
            return undefined;
        }
        return employees.find(emp => emp.id === user?.employeeID);
    }, [employees, user]);

    const defaultValues = useMemo((): Partial<FeeProposalFormValues> => {
        const requestDefaults = feeRequest ?? feeRequestDefaults;
        if (requestDefaults) {
            return {
                feeRequestId: feeRequest?.id ?? undefined,
                name: requestDefaults.feeName,
                school: resolveDefaultSchoolId(feeRequest, feeRequestDefaults, schools),
                year: resolveDefaultYearId(feeRequest, feeRequestDefaults, schoolYears),
                category: toIdString(requestDefaults.feeCategory),
                approvalRoute: toIdString(requestDefaults.approvalRoute),
                type: resolveDefaultType(feeRequest, requestDefaults.feeType),
                cost: requestDefaults.cost,
                costVariable: requestDefaults.costVariable === true,
                accountNumber: requestDefaults.accountCode,
                accountAssignedByBusinessOffice:
                    requestDefaults.accountAssignedByBusinessOffice === true,
                allowStudent: requestDefaults.allowStudent === true,
                allowEmployee: requestDefaults.allowEmployee === true,
                allowCommunityMember: requestDefaults.allowCommunityMember === true,
                allowMultiplePurchases: requestDefaults.allowMultiplePurchases === true,
                allowGuest: requestDefaults.allowGuest === true,
                description: requestDefaults.feeDescription,
                otherInformation: requestDefaults.otherInformation,
                requiresAdditionalInformation:
                    requestDefaults.courseQuestions.length > 0 ? "yes" : "no",
                questions: requestDefaults.courseQuestions
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(q => ({
                        ...q,
                        sortOrder: `${q.sortOrder ?? 1}`,
                    })),
                sponsors: requestDefaults.facultySponsors,
                ...(feeRequest
                    ? {
                          collectionStart: new Date(
                              feeRequest.saleStartDate.toLocalDateTime().toString(),
                          ),
                          collectionEnd: new Date(
                              feeRequest.saleEndDate.toLocalDateTime().toString(),
                          ),
                      }
                    : {}),
            };
        }
        return {
            name: "",
            costVariable: false,
            allowStudent: false,
            allowEmployee: false,
            allowCommunityMember: false,
            allowMultiplePurchases: false,
            accountAssignedByBusinessOffice: false,
            allowGuest: false,
            description: "",
            otherInformation: "",
            sponsors: currentEmployee ? [{id: currentEmployee.id, name: currentEmployee.name}] : [],
            questions: [],
        };
    }, [currentEmployee, feeRequest, feeRequestDefaults, schools, schoolYears]);

    const form = useForm<FeeProposalFormValues, any, FeeProposalRequestSchemaType>({
        defaultValues: defaultValues,
        resolver: zodResolver(feeProposalRequestSchema),
    });

    const selectableSchools = useMemo(() => {
        return withSavedOption(
            (schools ?? []).filter(isSelectableSchool),
            schools ?? [],
            defaultValues.school,
        );
    }, [schools, defaultValues.school]);

    const selectableSchoolYears = useMemo(() => {
        return withSavedOption(
            (schoolYears ?? []).filter(isSelectableSchoolYear),
            schoolYears ?? [],
            defaultValues.year,
        );
    }, [schoolYears, defaultValues.year]);

    const doSaveFeeProposal = async (data: FeeProposalRequestSchemaType) => {
        setSaving(true);
        try {
            const response = await saveFeeProposal(data);

            if (!response.ok) {
                setFeeNotification({
                    message: "Error Saving Request",
                    severity: "error",
                });
                setSaving(false);
                return;
            }

            const responseJson = (await response.json()) as SaveFeeResult;
            if (responseJson.errorCode && responseJson.errorMessage) {
                console.error("doSaveFeeProposal", responseJson);
                setFeeNotification({
                    message: responseJson.errorMessage,
                    severity: "warning",
                });
            } else {
                setFeeNotification({
                    message: "Request Saved",
                    severity: "success",
                });
            }
            setShow(false);
        } catch (error) {
            setSaving(false);
            setFeeNotification({
                message: "Error Saving Request",
                severity: "error",
            });
            console.error("doSaveFeeProposal", error);
        }
        setSaving(false);
        await refresh();
    };

    const watchType = form.watch("type", "");
    const watchAdditionalInfo = form.watch("requiresAdditionalInformation");

    useEffect(() => {
        if (show) {
            const sponsors = form.getValues("sponsors");

            if (sponsors.length === 0 && currentEmployee) {
                form.setValue("sponsors", [{id: currentEmployee.id, name: currentEmployee.name}]);
            }
        }
    }, [show]);

    useEffect(() => {
        form.reset(defaultValues);
    }, [feeRequest]);

    return (
        <>
            {show && loading && <CircularProgress />}

            {!loading && (
                <FormProvider {...form}>
                    <form
                        onSubmit={form.handleSubmit(async (data, event) => {
                            event?.stopPropagation();
                            await doSaveFeeProposal(data);
                        })}
                    >
                        <Dialog
                            open={show}
                            onClose={() => {
                                setShow(false);
                                setFeeRequest(null);
                                form.reset();
                            }}
                            aria-labelledby="alert-dialog-name"
                            fullWidth
                            maxWidth={"md"}
                            scroll="paper"
                        >
                            <DialogTitle id="alert-dialog-name">Fee Request</DialogTitle>
                            <DialogContent dividers>
                                <ReactMarkdown className="markdown">
                                    {notice.feeRequestInstructions}
                                </ReactMarkdown>
                                <Card
                                    sx={{
                                        backgroundColor: "#eee",
                                        borderRadius: "8px",
                                        p: 1,
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        <RhfTextField
                                            control={form.control}
                                            name={"feeRequestId"}
                                            sx={{display: "none"}}
                                        />
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <RhfTextField
                                                control={form.control}
                                                name={"name"}
                                                label={"Fee Name"}
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <RhfTextField
                                                select
                                                control={form.control}
                                                name="school"
                                                label="School"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                            >
                                                {selectableSchools.map(school => (
                                                    <MenuItem
                                                        key={`school-${school.id}`}
                                                        value={`${school.id}`}
                                                    >
                                                        {school.shortName}
                                                    </MenuItem>
                                                ))}
                                            </RhfTextField>
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <RhfTextField
                                                select
                                                control={form.control}
                                                name="year"
                                                label="Year"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                            >
                                                {selectableSchoolYears.map(schoolYear => (
                                                    <MenuItem
                                                        key={`school-year-${schoolYear.id}`}
                                                        value={`${schoolYear.id}`}
                                                    >
                                                        {schoolYear.year}
                                                    </MenuItem>
                                                ))}
                                            </RhfTextField>
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <RhfTextField
                                                select
                                                control={form.control}
                                                name="approvalRoute"
                                                label="Approval Route"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                            >
                                                {approvalRoutes &&
                                                    approvalRoutes.map(approvalRoute => (
                                                        <MenuItem
                                                            key={`approval-${approvalRoute.id}`}
                                                            value={`${approvalRoute.id}`}
                                                        >
                                                            {approvalRoute.route}
                                                        </MenuItem>
                                                    ))}
                                            </RhfTextField>
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <RhfTextField
                                                select
                                                control={form.control}
                                                name="type"
                                                label="Type"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                            >
                                                {defaultValues.type === "Course" && (
                                                    <MenuItem
                                                        key="type-course"
                                                        value="Course"
                                                        onClick={() =>
                                                            form.setValue("category", "")
                                                        }
                                                    >
                                                        Course
                                                    </MenuItem>
                                                )}
                                                <MenuItem
                                                    key="type-enrollment"
                                                    value="Enrollment"
                                                    onClick={() => form.setValue("category", "")}
                                                >
                                                    Enrollment
                                                </MenuItem>
                                                <MenuItem key="type-optional" value="Optional">
                                                    Optional
                                                </MenuItem>
                                            </RhfTextField>
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            {watchType === "Optional" && (
                                                <RhfTextField
                                                    select
                                                    control={form.control}
                                                    name="category"
                                                    label="Category"
                                                    sx={{
                                                        width: "100%",
                                                        backgroundColor: "#fff",
                                                    }}
                                                >
                                                    {feeCategories &&
                                                        feeCategories.map(feeCategory => (
                                                            <MenuItem
                                                                key={`approval-${feeCategory.id}`}
                                                                value={`${feeCategory.id}`}
                                                            >
                                                                {feeCategory.name}
                                                            </MenuItem>
                                                        ))}
                                                </RhfTextField>
                                            )}
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <RhfNumericTextField
                                                control={form.control}
                                                name="cost"
                                                label="Cost"
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                $
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                                sx={{
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                $1.00 minimum required, including for variable cost
                                                fees.
                                            </Typography>
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <FormControlLabel
                                                control={
                                                    <RhfCheckbox
                                                        control={form.control}
                                                        name="costVariable"
                                                    />
                                                }
                                                label="Variable Cost"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    mx: 0,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <FormControlLabel
                                                control={
                                                    <RhfCheckbox
                                                        control={form.control}
                                                        name="allowMultiplePurchases"
                                                    />
                                                }
                                                label="Allow Multiple Purchases"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    mx: 0,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <MuiDatePicker
                                                name="collectionStart"
                                                label="Collection Start"
                                                defaultValue={feeRequest?.saleStartDate}
                                                sx={{
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <MuiDatePicker
                                                name="collectionEnd"
                                                label="Collection End"
                                                defaultValue={feeRequest?.saleEndDate}
                                                sx={{
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid size={{xs: 12, md: 4}} sx={{p: 1}}>
                                            <RhfTextField
                                                control={form.control}
                                                name="accountNumber"
                                                label="Account Number"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 8}} sx={{p: 1, display: "flex"}}>
                                            <FormControlLabel
                                                control={
                                                    <RhfCheckbox
                                                        control={form.control}
                                                        name="accountAssignedByBusinessOffice"
                                                    />
                                                }
                                                label="To be Assigned by Business Office"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    mx: 0,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 6}} sx={{p: 1}}>
                                            <FormControlLabel
                                                control={
                                                    <RhfCheckbox
                                                        control={form.control}
                                                        name="allowStudent"
                                                    />
                                                }
                                                label="Available to Students / Parents"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    mx: 0,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 6}} sx={{p: 1}}>
                                            <FormControlLabel
                                                control={
                                                    <RhfCheckbox
                                                        control={form.control}
                                                        name="allowEmployee"
                                                    />
                                                }
                                                label="Available to Staff"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    mx: 0,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 6}} sx={{p: 1}}>
                                            <FormControlLabel
                                                control={
                                                    <RhfCheckbox
                                                        control={form.control}
                                                        name="allowCommunityMember"
                                                    />
                                                }
                                                label="Available to Community"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    mx: 0,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 6}} sx={{p: 1}}>
                                            <FormControlLabel
                                                control={
                                                    <RhfCheckbox
                                                        control={form.control}
                                                        name="allowGuest"
                                                    />
                                                }
                                                label="Available to Guests"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    mx: 0,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={12} sx={{p: 1}}>
                                            <RhfTextField
                                                control={form.control}
                                                name="description"
                                                label="Description (Visible to Fee Payers)"
                                                placeholder="Visible to Fee Payers"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                                multiline
                                                minRows={3}
                                                maxRows={10}
                                            />
                                        </Grid>
                                        <Grid size={12} sx={{p: 1}}>
                                            <RhfTextField
                                                control={form.control}
                                                name="otherInformation"
                                                label="Information for Bookstore, Business Office"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                                multiline
                                                minRows={3}
                                                maxRows={10}
                                            />
                                        </Grid>
                                        <Grid size={12} sx={{p: 1}}>
                                            <SponsorList
                                                form={form}
                                                sponsors={employees ?? []}
                                                selectedSponsors={form.getValues("sponsors") ?? []}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 12, md: 8}} sx={{p: 1}}>
                                            <RhfTextField
                                                select
                                                control={form.control}
                                                name="requiresAdditionalInformation"
                                                label="Does the fee require information to be collected from the student?"
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                }}
                                            >
                                                <MenuItem
                                                    key="requiresAdditionalInformation-yes"
                                                    value="yes"
                                                >
                                                    Yes
                                                </MenuItem>
                                                <MenuItem
                                                    key="requiresAdditionalInformation-no"
                                                    value="no"
                                                    onClick={() => form.setValue("questions", [])}
                                                >
                                                    No
                                                </MenuItem>
                                            </RhfTextField>
                                        </Grid>
                                        <Grid size={12} sx={{p: 1}}>
                                            {watchAdditionalInfo === "yes" && (
                                                <FeeQuestionsForm form={form} />
                                            )}
                                        </Grid>
                                    </Grid>
                                </Card>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    sx={{
                                        m: 2,
                                    }}
                                    onClick={() => {
                                        form.handleSubmit(doSaveFeeProposal)();
                                    }}
                                    disabled={saving || loading}
                                >
                                    Submit Request
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </form>
                </FormProvider>
            )}
        </>
    );
};

export default FeeProposalRequestForm;
